import queryString from 'query-string';
import cx from 'classnames';

import { Button, SearchInput } from '@ds';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import defaultTo from 'lodash/defaultTo';
import without from 'lodash/without';

import {
  GALLERY_TAB,
  SELECT_PANEL_PATH
} from '@experiment-management-shared/constants/visualizationConstants';

import {
  getBuildInCharts,
  TEMPLATE_SCOPE_DISPLAY_NAMES,
  TEMPLATE_SCOPE_TO_PARAMETER_KEY_MAP,
  TEMPLATE_SCOPE_TYPES,
  TEMPLATE_SCOPE_TYPES_BY_LABEL,
  PANEL_CODE_LANGUAGES
} from '@experiment-management-shared/constants/chartsGallery';

import useImportPanelMutation from '@API/panels/useImportPanelMutation';
import GalleryTable from '@experiment-management-shared/components/PanelModal/GalleryTable/GalleryTable';
import visualizationsActions from '@/actions/visualizationsActions';
import { dialogTypes } from '@/constants/alertTypes';
import { getIsUserLoggedIn } from '@/reducers/userReducer';
import alertsUtil from '@/util/alertsUtil';

import { useActiveWorkspace } from '@shared/hooks';
import ImportFileModal from '@experiment-management-shared/components/ImportFileModal';
import SmallLoader from '@shared/components/SmallLoader';
import StyledTabs from '@shared/components/StyledComponents/StyledTabs';
import StyledTab from '@shared/components/StyledComponents/StyledTab';
import TabLabelWithBadge from '@shared/components/TabLabelWithBadge';
import { DSImportIcon, DSSearchIcon } from '@ds-icons';
import usePanelTemplates from '@experiment-management-shared/api/library-panel/usePanelTemplates';

const { WORKSPACE, BUILT_IN } = TEMPLATE_SCOPE_TYPES;

const hashWithGalleryTabParam = `${SELECT_PANEL_PATH}?${GALLERY_TAB}`;

const GalleryModal = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const importPanelMutation = useImportPanelMutation();
  const buildInCharts = useSelector(state => getBuildInCharts(state));

  const {
    data: workspaceTemplates,
    isLoading: isLoadingWorkspace
  } = usePanelTemplates({
    scope: WORKSPACE,
    filterLanguages: [
      PANEL_CODE_LANGUAGES.PYTHON,
      PANEL_CODE_LANGUAGES.JAVASCRIPT
    ]
  });

  const isUserLoggedIn = useSelector(getIsUserLoggedIn);
  const activeWorkspace = useActiveWorkspace();

  const parsedHash = queryString.parse(location.hash);
  const tabLabelFromParam = parsedHash[hashWithGalleryTabParam];
  const currentTabFromParam = TEMPLATE_SCOPE_TYPES_BY_LABEL[tabLabelFromParam];

  const getDefaultActiveTab = () => {
    if (currentTabFromParam) {
      return currentTabFromParam;
    }

    return TEMPLATE_SCOPE_TYPES.BUILT_IN;
  };

  const [activeTab, setActiveTab] = useState(getDefaultActiveTab());
  const [searchText, setSearchText] = useState('');

  const showControls = activeTab !== TEMPLATE_SCOPE_TYPES.BUILT_IN;
  const centerTabContent = activeTab === TEMPLATE_SCOPE_TYPES.BUILT_IN;

  useEffect(() => {
    if (!currentTabFromParam && activeTab) {
      const hash = `#${hashWithGalleryTabParam}=${TEMPLATE_SCOPE_TO_PARAMETER_KEY_MAP[activeTab]}`;
      history.replace({ ...location, hash }, { preventScroll: true });
      return;
    }

    if (currentTabFromParam !== activeTab) {
      setActiveTab(currentTabFromParam);
    }

    dispatch(visualizationsActions.setSavedGalleryTab(activeTab));
  }, [activeTab, currentTabFromParam, dispatch, history, location]);

  useEffect(() => setSearchText(''), [tabLabelFromParam]);

  const templatesByTabLabel = useMemo(() => {
    const templateByLabelMap = {
      [BUILT_IN]: buildInCharts,
      [WORKSPACE]: workspaceTemplates
    };

    return templateByLabelMap[activeTab] || [];
  }, [activeTab, workspaceTemplates]);

  const chartTemplates = useMemo(() => {
    return templatesByTabLabel.filter(({ name, description, owner }) => {
      const searchableContent = [name, description, owner].map(content =>
        defaultTo(content, '').toLowerCase()
      );

      return searchableContent.some(content =>
        content.includes(searchText.toLowerCase())
      );
    });
  }, [templatesByTabLabel, searchText]);

  const handleCancelSearch = useCallback(() => setSearchText(''), []);
  const handleSearchChange = useCallback(text => setSearchText(text), []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    dispatch(visualizationsActions.setSavedGalleryTab(newValue));

    const hash = `#${hashWithGalleryTabParam}=${TEMPLATE_SCOPE_TO_PARAMETER_KEY_MAP[newValue]}`;

    history.replace({ ...location, hash }, { preventScroll: true });
  };

  const handleTemplatesChange = () => {
    queryClient.invalidateQueries([
      'panelTemplates',
      { teamId: activeWorkspace?.id }
    ]);
    queryClient.invalidateQueries([
      'panelTemplatesCount',
      { teamId: activeWorkspace?.id }
    ]);
  };

  const getTabCount = tabName => {
    if (tabName === TEMPLATE_SCOPE_DISPLAY_NAMES.BUILT_IN) {
      return buildInCharts.length;
    }

    if (TEMPLATE_SCOPE_DISPLAY_NAMES.WORKSPACE) {
      return workspaceTemplates?.length;
    }

    return 0;
  };

  const openImportTemplateModal = () => {
    const modalId = dialogTypes.IMPORT_FILE_MODAL;

    const handleSubmit = file => {
      const templateToImport = new FormData();
      templateToImport.append('file', file);

      importPanelMutation.mutate({ templateToImport });
    };

    const modal = (
      <ImportFileModal
        modalId={modalId}
        title="Import Panel"
        submitHandler={handleSubmit}
      />
    );

    dispatch(alertsUtil.openCustomModal(modalId, modal));
  };

  const renderTabs = () => {
    let tabs = [BUILT_IN, WORKSPACE];

    if (!isUserLoggedIn) {
      tabs = without(tabs, WORKSPACE);
    }

    return tabs.map(scopeType => {
      const tabName = TEMPLATE_SCOPE_DISPLAY_NAMES[scopeType];
      const label = (
        <TabLabelWithBadge
          labelText={tabName}
          badgeCount={getTabCount(tabName)}
        />
      );

      return <StyledTab key={tabName} value={scopeType} label={label} />;
    });
  };

  const renderTabBar = () => {
    return (
      <StyledTabs
        className={`charts-gallery-tab-bar ${TEMPLATE_SCOPE_DISPLAY_NAMES[activeTab]}`}
        value={activeTab}
        onChange={handleTabChange}
      >
        {renderTabs()}
      </StyledTabs>
    );
  };

  const renderActiveTab = () => {
    const panelType =
      activeTab === TEMPLATE_SCOPE_TYPES.WORKSPACE
        ? 'Panel library'
        : TEMPLATE_SCOPE_DISPLAY_NAMES[activeTab];

    const isLoadingMap = {
      [WORKSPACE]: isLoadingWorkspace
    };

    if (isLoadingMap[activeTab]) {
      const loadingMessage = `Fetching ${panelType} panels`;

      return (
        <SmallLoader
          isFlexDisplay
          primaryMessage="Loading..."
          secondaryMessage={loadingMessage}
        />
      );
    }

    return (
      <div
        className={cx('gallery-modal-tab-content', {
          centered: centerTabContent
        })}
      >
        {showControls && (
          <div className="gallery-modal-tab-controls-container">
            <div className="gallery-modal-tab-controls">
              <div className="gallery-modal-tab-controls-search">
                <SearchInput
                  placeholder="Search..."
                  onChange={e => handleSearchChange(e.currentTarget.value)}
                  value={searchText}
                  PrefixIcon={DSSearchIcon}
                  size="small"
                  onClearIconClick={handleCancelSearch}
                />
              </div>
            </div>
            <Button
              PrefixIcon={<DSImportIcon />}
              onClick={openImportTemplateModal}
              type="secondary"
            >
              Import
            </Button>
          </div>
        )}

        <GalleryTable
          activeTab={activeTab}
          chartTemplates={chartTemplates}
          onChangeTemplate={handleTemplatesChange}
          isSearchText={searchText !== ''}
        />
      </div>
    );
  };

  const renderSubHeader = () => {
    return (
      <div className="gallery-modal-subheader">
        <div className="gallery-modal-tabs">{renderTabBar()}</div>
      </div>
    );
  };

  return (
    <div className="gallery-modal-body">
      {renderSubHeader()}
      {renderActiveTab()}
    </div>
  );
};

export default GalleryModal;
